import { useCallback, useRef } from "react"

/**
 * This is a beast of a function! It allows to call a callback after a debounce delay. This can be used in conjunction with
 * an event handler to passthrough live typing values and then execute a secondary function after X milliseconds.
 *
 * @param callback - The callback to execute after the debounce delay.
 * @param delay - The delay in milliseconds before the callback is executed.
 * @returns A debounced callback function that can be used in conjunction with an event handler.
 */
export function useDebouncedCallback<T>(callback: (value: T) => void | Promise<void>, delay: number) {
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>()

  return useCallback(
    (value: T) => {
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      // Set new timeout
      timeoutRef.current = setTimeout(() => {
        void callback(value)
      }, delay)
    },
    [callback, delay],
  )
}
